/**
 * Ratschlag des Ministeriums im Gesetzentwurf: Welche einzelne Änderung räumt die wichtigste Hürde aus?
 *
 * Reine Anzeige, keine Spielregel: Es werden nur Vorschauen gerechnet (dieselben Funktionen wie in der Vorschau),
 * kein Zufall, nichts wird gespeichert. Geprüft werden wenige gezielte Varianten: jede Gegenfinanzierung und je
 * Stellschraube die Nachbarwerte (Regler ±1 Schritt), die anderen Optionen bzw. Ja/Nein.
 */
import { S } from './state';
import { since } from './version';
import { draftFx, paramMax } from './novelle9';
import { btVote, brVote, cabinetCheck, approval } from './govern';
import { needFor } from './longterm';
import { score, PN, PA } from './rules';
import { debtLimit } from './budget';
import { zgFit, ZG_WEIGHT, FIN_OPTS, FIN_KEY, FIN_MIN, ownFinancing } from './v2';
import { GROUPS, WEIGHT } from '../content/axes';
import { de, sg } from './util';
import { ISS } from '../content/states';
import { tplAvail } from './novelle9';
import { canDraft } from './coalition';

type Eval = { yes: number; nd: number; cabBad: string[]; cabGr: string[]; brOk: boolean; ys: number; b: number; after: number; appr: number; byNo: Record<string, number> };
type Variant = { p: Record<string, any>; what: string };
export type Advice = { k: 'bt' | 'cab' | 'br' | 'budget' | 'prog'; t: string } | null;

function evalP(T: any, iss: string, tpl: number, p: Record<string, any>, lawId: number | null | undefined, viewer: string): Eval {
  const fx: any = draftFx(T, iss, tpl, p, lawId);
  const v = btVote(fx.pos), nd = needFor(fx), cab = cabinetCheck(fx).filter((c: any) => c.p !== viewer);
  const br = (T.br || fx.gg) ? brVote(fx.pos, false) : null;
  const zg = since(2) ? zgFit(fx.pos || {}).tot * ZG_WEIGHT : 0;
  const appr = Object.keys(GROUPS).reduce((s, g) => s + (WEIGHT[g] || 0) * (Math.abs(fx.g?.[g] || 0) >= .5 ? fx.g[g] : 0), 0) + (Math.abs(zg) >= .1 ? zg : 0);
  const byNo: Record<string, number> = {}; for (const id in v.by) if (!v.by[id].y) byNo[id] = v.by[id].s;
  return { yes: v.yes, nd, cabBad: cab.filter((c: any) => c.st !== 'ok' && c.st !== 'grumble').map((c: any) => c.p), cabGr: cab.filter((c: any) => c.st === 'grumble').map((c: any) => c.p),
    brOk: br === null || br > (fx.gg ? 2 / 3 : .5), ys: score(viewer, fx.pos), b: fx.b || 0, after: S.saldo + (fx.b || 0), appr, byNo };
}

function variants(T: any, iss: string, tpl: number, p: Record<string, any>, lawId: number | null | undefined, chan: boolean): Variant[] {
  const out: Variant[] = [];
  for (const pa of T.ps || []) {
    const v = p[pa.id];
    if (pa.k === 'r') {
      const max = paramMax(iss, tpl, pa, lawId);
      for (const d of [-1, 1]) { const n = Math.round((v + d * pa.st) / pa.st) * pa.st; if (n >= pa.min && n <= max && n !== v) out.push({ p: { ...p, [pa.id]: n }, what: `„${pa.l}“ auf ${de(n, pa.st < 1 ? 1 : 0)}${pa.u ? ' ' + String(pa.u).trim() : ''}` }); }
    } else if (pa.k === 'c') { pa.o.forEach((o: string, i: number) => { if (i !== v) out.push({ p: { ...p, [pa.id]: i }, what: `„${pa.l}“ auf „${o}“` }); }); }
    else out.push({ p: { ...p, [pa.id]: v ? 0 : 1 }, what: `„${pa.l}“ ${v ? 'abwählen' : 'wählen'}` });
  }
  if (chan && since(2) && !ownFinancing(T) && -(T.fx(p).b || 0) >= FIN_MIN) {
    const cur = Number(p[FIN_KEY]) || 0;
    FIN_OPTS.forEach((o, i) => { if (i !== cur) out.push({ p: { ...p, [FIN_KEY]: i }, what: `Gegenfinanzierung „${o}“` }); });
  }
  return out;
}

/** Ein Ratschlag oder null, wenn es keine lohnende Einzeländerung gibt. */
export function advise(T: any, iss: string, tpl: number, p: Record<string, any>, lawId: number | null | undefined, viewer = 'YOU'): Advice {
  if (!T || T.custom) return null;
  const chan = viewer === 'YOU', lim = debtLimit();
  let cur: Eval; try { cur = evalP(T, iss, tpl, p, lawId, viewer); } catch { return null; }
  const vs = variants(T, iss, tpl, p, lawId, chan).map((x) => { try { return { ...x, e: evalP(T, iss, tpl, x.p, lawId, viewer) }; } catch { return null; } }).filter(Boolean) as (Variant & { e: Eval })[];
  if (!vs.length) return null;
  const ok = (e: Eval) => ({ bt: e.yes >= e.nd, cab: !e.cabBad.length, br: e.brOk });
  /** Variante darf keine andere Hürde aufreißen. */
  const safe = (e: Eval) => { const a = ok(cur), b = ok(e); return (!a.bt || b.bt) && (!a.cab || b.cab) && (!a.br || b.br); };
  const best = (xs: (Variant & { e: Eval })[], f: (e: Eval) => number) => xs.sort((a, b) => f(b.e) - f(a.e))[0];
  const tail = (e: Eval) => { const d = e.appr - cur.appr; return Math.abs(d) >= .3 ? ` Die Zustimmung ${d > 0 ? 'steigt' : 'sinkt'} dabei um etwa ${de(Math.abs(d), 1)} Punkte.` : ''; };

  // 1. Bundestag
  if (cur.yes < cur.nd) {
    const fix = vs.filter((x) => x.e.yes >= x.e.nd && safe(x.e));
    if (fix.length) { const x = best(fix, (e) => e.appr + e.ys); const won = Object.keys(cur.byNo).filter((id) => !(id in x.e.byNo));
      return { k: 'bt', t: `Mit ${x.what} bekämen wir die Mehrheit im Bundestag${won.length ? `, dann stimmt auch ${won.map(PN).join(' und ')} zu` : ''}.${tail(x.e)}` }; }
    const x = best(vs.filter((x) => safe(x.e)), (e) => e.yes);
    if (x && x.e.yes > cur.yes) return { k: 'bt', t: `${x.what[0].toUpperCase()}${x.what.slice(1)} brächte ${x.e.yes - cur.yes} Stimmen mehr, für die Mehrheit reicht das noch nicht.` };
    // anderer Ansatz mit Mehrheit (Standardeinstellungen)
    const alt = (ISS[iss]?.laws || []).map((t: any, k: number) => [k, t] as const).filter(([k, t]: any) => k !== tpl && !t.custom && tplAvail(t) && canDraft(viewer, t)).map(([k, t]: any) => {
      try { const e = evalP(t, iss, k, Object.fromEntries(t.ps.map((x: any) => [x.id, x.d])), lawId, viewer); return e.yes >= e.nd ? { t, e } : null; } catch { return null; } }).filter(Boolean) as { t: any; e: Eval }[];
    if (alt.length) { const a = alt.sort((x, y) => (y.e.ys + y.e.appr / 10) - (x.e.ys + x.e.appr / 10))[0]; return { k: 'bt', t: `Mit diesem Ansatz wird es schwer. Der Ansatz „${a.t.t}“ hätte eine Mehrheit im Bundestag (${a.e.yes} Stimmen).` }; }
    // nächste Partei, die fast zustimmt
    const near = Object.entries(cur.byNo).sort((a, b) => b[1] - a[1])[0];
    return near ? { k: 'bt', t: `Am nächsten dran ist ${PN(near[0])}. Ein Ansatz, der näher an deren Programm liegt, hätte bessere Chancen.` } : null;
  }
  // 2. Kabinett
  if (cur.cabBad.length) {
    const fix = vs.filter((x) => !x.e.cabBad.length && safe(x.e));
    if (fix.length) { const x = best(fix, (e) => e.appr + e.ys); return { k: 'cab', t: `Mit ${x.what} trägt ${cur.cabBad.map(PA).join(' und ')} das im Kabinett mit.${tail(x.e)}` }; }
  }
  // 3. Bundesrat
  if (!cur.brOk) {
    const fix = vs.filter((x) => x.e.brOk && safe(x.e));
    if (fix.length) { const x = best(fix, (e) => e.appr); return { k: 'br', t: `Mit ${x.what} hätten wir im Bundesrat eine Mehrheit.${tail(x.e)}` }; }
  }
  // 4. Haushalt
  if (cur.b < -.05 && cur.after < lim) {
    const pushes = S.saldo >= lim;
    const fix = vs.filter((x) => safe(x.e) && (pushes ? x.e.after >= lim : x.e.b > cur.b + .5));
    if (fix.length) { const x = best(fix, (e) => e.b * 2 + e.appr);
      return { k: 'budget', t: pushes ? `Mit ${x.what} blieben wir unter der Schuldenbremse.${tail(x.e)}` : `Mit ${x.what} kostet es ${Math.abs(x.e.b) < .05 ? 'nichts' : `nur ${sg(x.e.b)} Mrd`} im Jahr statt ${sg(cur.b)} Mrd.${tail(x.e)}` }; }
  }
  // 5. Programm
  if (cur.ys < .75) {
    const fix = vs.filter((x) => safe(x.e) && x.e.ys >= Math.min(.75, cur.ys + .05) && x.e.yes >= x.e.nd);
    if (fix.length) { const x = best(fix, (e) => e.ys * 10 + e.appr); return { k: 'prog', t: `Mit ${x.what} passt es besser zum Programm (${Math.round(x.e.ys * 100)} statt ${Math.round(cur.ys * 100)} %).${tail(x.e)}` }; }
  }
  return null;
}
